import React from 'react';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { MessageCrudService, MessagePage, PaginationOptions } from '../services/messages/message-crud.service';
import { CopilotMessage } from '../types/database-types';
import { queryKeys } from '@/lib/query-keys';
import { logInfo, LogCategory } from '@/utils/logger';

interface UsePaginatedMessagesOptions {
  threadId: string;
  pageSize?: number;
  enabled?: boolean;
}

interface UsePaginatedMessagesReturn {
  messages: CopilotMessage[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  fetchNextPage: () => void;
  fetchPreviousPage: () => void;
  isFetchingNextPage: boolean;
  isFetchingPreviousPage: boolean;
  totalCount: number;
  loadedCount: number;
  isInitialLoading: boolean;
}

/**
 * Hook for managing paginated messages with virtual scrolling support
 * Implements bidirectional infinite scrolling for chat interfaces
 */
export function usePaginatedMessages({
  threadId,
  pageSize = 20,
  enabled = true
}: UsePaginatedMessagesOptions): UsePaginatedMessagesReturn {
  
  const [isFetchingPreviousPage, setIsFetchingPreviousPage] = useState(false);
  const allMessagesRef = useRef<CopilotMessage[]>([]);
  const isInitialLoadRef = useRef(true);

  // Infinite query for newer messages (forward pagination)
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch
  } = useInfiniteQuery({
    queryKey: queryKeys.copilot.messagesPaginated(threadId),
    queryFn: async ({ pageParam }) => {
      const options: PaginationOptions = {
        limit: pageSize,
        cursor: pageParam as string | undefined,
        direction: 'backward' // Get older messages by default
      };
      
      const result = await MessageCrudService.fetchMessagesPaginated(threadId, options);
      
      // Log pagination info
      logInfo('Paginated messages fetched', {
        category: LogCategory.DATABASE,
        data: {
          threadId,
          pageSize,
          messagesCount: result.messages.length,
          hasMore: result.hasMore,
          totalCount: result.totalCount,
          direction: 'backward'
        }
      });
      
      return result;
    },
    getNextPageParam: (lastPage: MessagePage) => {
      return lastPage.hasMore ? lastPage.nextCursor : undefined;
    },
    initialPageParam: undefined,
    enabled: enabled && !!threadId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch previous page (newer messages) manually
  const fetchPreviousPage = useCallback(async () => {
    if (isFetchingPreviousPage || !allMessagesRef.current.length) return;
    
    setIsFetchingPreviousPage(true);
    
    try {
      const firstMessage = allMessagesRef.current[0];
      if (!firstMessage) return;
      
      const options: PaginationOptions = {
        limit: pageSize,
        cursor: firstMessage.createdAt,
        direction: 'forward' // Get newer messages
      };
      
      const result = await MessageCrudService.fetchMessagesPaginated(threadId, options);
      
      if (result.messages.length > 0) {
        // Prepend new messages to the beginning
        allMessagesRef.current = [...result.messages, ...allMessagesRef.current];
        
        logInfo('Previous page (newer messages) fetched', {
          category: LogCategory.DATABASE,
          data: {
            threadId,
            newMessagesCount: result.messages.length,
            totalLoadedCount: allMessagesRef.current.length
          }
        });
      }
    } catch (error) {
      console.error('Error fetching previous page:', error);
    } finally {
      setIsFetchingPreviousPage(false);
    }
  }, [threadId, pageSize, isFetchingPreviousPage]);

  // Combine all pages into a single array
  const messages = React.useMemo(() => {
    if (!data?.pages) return [];
    
    // Flatten all pages and combine with manually loaded previous messages
    const paginatedMessages = data.pages.flatMap(page => page.messages);
    
    // Combine and deduplicate by message ID
    const combinedMessages = [...allMessagesRef.current, ...paginatedMessages];
    const uniqueMessages = combinedMessages.filter((message, index, self) => 
      index === self.findIndex(m => m.id === message.id)
    );
    
    // Sort by creation time (oldest first)
    uniqueMessages.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    
    // Update ref for next operations
    allMessagesRef.current = uniqueMessages;
    
    return uniqueMessages;
  }, [data?.pages]);

  // Get metadata from the last page
  const lastPage = data?.pages?.[data.pages.length - 1];
  const totalCount = lastPage?.totalCount || 0;
  const loadedCount = messages.length;
  const hasPreviousPage = loadedCount < totalCount;

  // Reset when threadId changes
  useEffect(() => {
    allMessagesRef.current = [];
    isInitialLoadRef.current = true;
    setIsFetchingPreviousPage(false);
  }, [threadId]);

  // Mark initial load as complete
  useEffect(() => {
    if (data?.pages && data.pages.length > 0 && isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
    }
  }, [data?.pages]);

  return {
    messages,
    isLoading,
    isError,
    error: error as Error | null,
    hasNextPage: !!hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    totalCount,
    loadedCount,
    isInitialLoading: isLoading && isInitialLoadRef.current
  };
} 